import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MushakFormsService } from '../../services/mushakForms.service';
import { StyleService } from '../../services/style.service';
import { ItemService } from '../../services/item.service';
import { AuthService } from '../../services/auth.service';
import {
  Coefficient, CoefficientInputLine, CoefficientProductKind, CoefficientStatus, CoefficientSummary, ConsumptionCheck,
  VALUE_ADD_KINDS, ValueAddKind,
} from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';
import SearchableSelect from '../common/SearchableSelect';
import OrderPicker from '../costing/OrderPicker';

interface PickOption { id: string; label: string; unit?: string; hsCode?: string }

const STATUS_COLOR: Record<CoefficientStatus, 'default' | 'warning' | 'success' | 'info'> = {
  DRAFT: 'default', SUBMITTED: 'warning', ACCEPTED: 'success', SUPERSEDED: 'info',
};

interface EditLine { itemId: string; itemName: string; uom: string; quantityPerUnit: string; wastagePercent: string; unitPurchasePrice: string }
interface EditVa { kind: ValueAddKind; amountPerUnit: string; note: string }

const num = (v: string) => (v === '' || Number.isNaN(Number(v)) ? 0 : Number(v));
const lineValue = (l: EditLine) => {
  const q = num(l.quantityPerUnit);
  return (q + (q * num(l.wastagePercent)) / 100) * num(l.unitPurchasePrice);
};
const toEdit = (l: CoefficientInputLine): EditLine => ({
  itemId: l.itemId, itemName: l.itemName, uom: l.uom, quantityPerUnit: String(l.quantityPerUnit),
  wastagePercent: String(l.wastagePercent), unitPurchasePrice: String(l.unitPurchasePrice),
});

const MushakCoefficientPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const canManage = usePermission(PERMISSIONS.INDIRECT_TAX_MANAGE);

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<CoefficientSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // editor
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Coefficient | null>(null);
  const [kind, setKind] = useState<CoefficientProductKind>('STYLE');
  const [productId, setProductId] = useState('');
  const [productOption, setProductOption] = useState<PickOption | null>(null);
  const [productOptions, setProductOptions] = useState<PickOption[]>([]);
  const [unit, setUnit] = useState('PCS');
  const [hsCode, setHsCode] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(toLocalIsoDate());
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<EditLine[]>([]);
  const [valueAdds, setValueAdds] = useState<EditVa[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [itemOptions, setItemOptions] = useState<PickOption[]>([]);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [officeRef, setOfficeRef] = useState('');
  const [busy, setBusy] = useState(false);

  // consumption check
  const [orderId, setOrderId] = useState('');
  const [check, setCheck] = useState<ConsumptionCheck | null>(null);

  const readOnly = !!current && current.status !== 'DRAFT';
  const editable = canManage && !readOnly;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await MushakFormsService.listCoefficients(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load Mushak 4.3 declarations');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const searchProducts = async (text: string, k: CoefficientProductKind = kind) => {
    try {
      if (k === 'STYLE') {
        const page = await StyleService.list(institutionId, 0, 20, undefined, undefined, text, true);
        setProductOptions(page.content.map(s => ({ id: s.id, label: `${s.styleNumber} - ${s.name}`, hsCode: (s as { hsCode?: string }).hsCode })));
      } else {
        const page = await ItemService.getItemsByInstitution(0, 20, text);
        setProductOptions(page.content.map(i => ({ id: i.id as string, label: `${i.itemCode} - ${i.itemName}`, unit: i.unit?.symbol, hsCode: i.hsCode })));
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to search products');
    }
  };

  // L7: debounce the typed search; the first (empty-text) load is immediate
  const productTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (productTimer.current) clearTimeout(productTimer.current);
    if (itemTimer.current) clearTimeout(itemTimer.current);
  }, []);
  const debouncedSearchProducts = (text: string, k: CoefficientProductKind = kind) => {
    if (productTimer.current) clearTimeout(productTimer.current);
    productTimer.current = setTimeout(() => { searchProducts(text, k); }, 300);
  };
  const debouncedSearchItems = (text: string) => {
    if (itemTimer.current) clearTimeout(itemTimer.current);
    itemTimer.current = setTimeout(() => { searchItems(text); }, 300);
  };

  const searchItems = async (text: string) => {
    try {
      const page = await ItemService.getItemsByInstitution(0, 20, text);
      setItemOptions(page.content.map(i => ({ id: i.id as string, label: `${i.itemCode} - ${i.itemName}`, unit: i.unit?.symbol })));
    } catch (e) {
      toast.showApiError(e, 'Failed to search items');
    }
  };

  const reset = () => {
    setCurrent(null); setKind('STYLE'); setProductId(''); setProductOption(null); setProductOptions([]);
    setUnit('PCS'); setHsCode(''); setEffectiveFrom(toLocalIsoDate()); setPrice(''); setNotes('');
    setLines([]); setValueAdds([]); setWarnings([]);
  };

  const openNew = () => { reset(); setOpen(true); searchProducts('', 'STYLE'); searchItems(''); };

  const openExisting = async (id: string) => {
    try {
      const c = await MushakFormsService.getCoefficient(institutionId, id);
      reset();
      setCurrent(c);
      setKind(c.productKind);
      setProductId((c.productKind === 'STYLE' ? c.styleId : c.itemId) ?? '');
      setProductOption({ id: (c.productKind === 'STYLE' ? c.styleId : c.itemId) ?? '', label: c.productName });
      setUnit(c.unitOfSupply); setHsCode(c.hsCode ?? ''); setEffectiveFrom(c.effectiveFrom);
      setPrice(String(c.supplyPricePerUnit)); setNotes(c.notes ?? '');
      setLines(c.inputs.map(toEdit));
      setValueAdds(c.valueAdds.map(v => ({ kind: v.kind, amountPerUnit: String(v.amountPerUnit), note: v.note ?? '' })));
      setOpen(true);
      if (c.status === 'DRAFT') { searchProducts('', c.productKind); searchItems(''); }
    } catch (e) {
      toast.showApiError(e, 'Failed to open declaration');
    }
  };

  const doPrefill = async () => {
    if (!productId) return;
    try {
      const p = await MushakFormsService.prefill(institutionId, kind === 'STYLE' ? 'style' : 'item', productId);
      setHsCode(p.hsCode ?? hsCode);
      if (p.unitOfSupply) setUnit(p.unitOfSupply);
      if (p.inputs.length) setLines(p.inputs.map(toEdit));
      setWarnings(p.warnings);
      toast.showSuccess(kind === 'STYLE' ? 'Prefilled from the style BOM - review prices and wastage' : 'Item details loaded');
    } catch (e) {
      toast.showApiError(e, 'Failed to prefill');
    }
  };

  const payload = () => ({
    productKind: kind,
    itemId: kind === 'ITEM' ? productId : null,
    styleId: kind === 'STYLE' ? productId : null,
    unitOfSupply: unit, hsCode: hsCode || null, effectiveFrom, supplyPricePerUnit: num(price), notes: notes || null,
    inputs: lines.filter(l => l.itemId).map(l => ({
      itemId: l.itemId, quantityPerUnit: num(l.quantityPerUnit), wastagePercent: num(l.wastagePercent),
      unitPurchasePrice: num(l.unitPurchasePrice), uom: l.uom || null,
    })),
    valueAdds: valueAdds.map(v => ({ kind: v.kind, amountPerUnit: num(v.amountPerUnit), note: v.note || null })),
  });

  const run = async (fn: () => Promise<Coefficient | void>, ok: string, close = false) => {
    try {
      setBusy(true);
      const res = await fn();
      toast.showSuccess(ok);
      await load();
      if (close || !res) { setOpen(false); } else { await openExisting((res as Coefficient).id); }
    } catch (e) {
      toast.showApiError(e, 'The action failed');
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => (current
    ? MushakFormsService.updateCoefficient(institutionId, current.id, payload())
    : MushakFormsService.createCoefficient(institutionId, payload())), 'Declaration saved');

  const download = async (id: string, kindOfFile: 'pdf' | 'xlsx') => {
    try {
      saveBlob(await MushakFormsService.downloadCoefficient(institutionId, id, kindOfFile), `mushak-4-3-${id}.${kindOfFile}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download');
    }
  };

  const runCheck = async () => {
    if (!orderId) return;
    try {
      setCheck(await MushakFormsService.consumptionCheck(institutionId, orderId));
    } catch (e) {
      setCheck(null);
      toast.showApiError(e, 'Failed to run the consumption check');
    }
  };

  const inputCost = lines.reduce((a, l) => a + lineValue(l), 0);
  const addCost = valueAdds.reduce((a, v) => a + num(v.amountPerUnit), 0);

  return (
    <Box data-help-page="tax.coefficient.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Mushak 4.3 - Input-Output Coefficient Declarations</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Declare the inputs per unit of output and the value addition before the first supply and whenever the ratio or
        price changes. The printed layout is marked verify - check it against the current NBR form with your VAT consultant.
      </Alert>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab data-help="tax.coefficient.tabDeclarations" label="Declarations" />
        <Tab data-help="tax.coefficient.tabCheck" label="Consumption check" />
      </Tabs>

      {tab === 0 && (
        <>
          {canManage && <Button data-help="tax.coefficient.new" variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ mb: 2 }}>New declaration</Button>}
          {loading ? <CircularProgress /> : (
            <Paper sx={{ overflowX: 'auto' }}>
              <Table data-help-section="tax.coefficient.table" size="small" data-testid="coefficient-list">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="tax.coefficient.col.product">Product</TableCell><TableCell data-help="tax.coefficient.col.version">Version</TableCell><TableCell data-help="tax.coefficient.col.unit">Unit</TableCell>
                    <TableCell data-help="tax.coefficient.col.effective">Effective from</TableCell><TableCell data-help="tax.coefficient.col.price" align="right">Supply price</TableCell>
                    <TableCell data-help="tax.coefficient.col.status">Status</TableCell><TableCell data-help="tax.coefficient.col.officeRef">VAT office ref</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && <TableRow><TableCell colSpan={8}>No declarations yet.</TableCell></TableRow>}
                  {rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.productName}</TableCell><TableCell>v{r.versionNo}</TableCell><TableCell>{r.unitOfSupply}</TableCell>
                      <TableCell>{formatDate(r.effectiveFrom)}</TableCell>
                      <TableCell align="right">{formatMoney(r.supplyPricePerUnit)}</TableCell>
                      <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={r.status} /></TableCell>
                      <TableCell>{r.vatOfficeReference ?? ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Button data-help="tax.coefficient.action.open" size="small" onClick={() => openExisting(r.id)}>{canManage && r.status === 'DRAFT' ? 'Edit' : 'View'}</Button>
                        <Button data-help="tax.coefficient.action.pdf" size="small" onClick={() => download(r.id, 'pdf')}>PDF</Button>
                        <Button data-help="tax.coefficient.action.xlsx" size="small" onClick={() => download(r.id, 'xlsx')}>XLSX</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}

      {tab === 1 && (
        <Paper data-help-section="tax.coefficient.check" sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Compares each style's accepted declaration (input per unit plus declared wastage x the order's finished quantity)
            with the material consumption tagged to the order. Read-only.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 260px', maxWidth: 420 }}>
              <OrderPicker data-help="tax.coefficient.check.order" value={orderId} onChange={setOrderId} always openOnly={false} label="Buyer order" />
            </Box>
            <Button data-help="tax.coefficient.runCheck" variant="outlined" onClick={runCheck} disabled={!orderId}>Run check</Button>
          </Box>
          {check && (
            <Box sx={{ overflowX: 'auto' }}>
              <Typography variant="subtitle2">Order {check.orderNumber}: {check.flaggedCount} line(s) flagged</Typography>
              <Table data-help-section="tax.coefficient.checkTable" size="small" data-testid="consumption-check">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="tax.coefficient.check.col.item">Item</TableCell><TableCell data-help="tax.coefficient.check.col.style">Style</TableCell><TableCell data-help="tax.coefficient.check.col.finished" align="right">Finished qty</TableCell>
                    <TableCell data-help="tax.coefficient.check.col.declared" align="right">Declared</TableCell><TableCell data-help="tax.coefficient.check.col.allowed" align="right">Allowed (incl. wastage)</TableCell>
                    <TableCell data-help="tax.coefficient.check.col.actual" align="right">Actual</TableCell><TableCell data-help="tax.coefficient.check.col.over" align="right">Over</TableCell><TableCell data-help="tax.coefficient.check.col.note">Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {check.lines.map(l => (
                    <TableRow key={l.itemId} sx={l.flagged ? { bgcolor: 'warning.light' } : undefined}>
                      <TableCell>{l.itemName} ({l.uom})</TableCell><TableCell>{l.styleName}</TableCell>
                      <TableCell align="right">{l.finishedQuantity}</TableCell><TableCell align="right">{l.declaredQuantity}</TableCell>
                      <TableCell align="right">{l.allowedQuantity}</TableCell><TableCell align="right">{l.actualQuantity}</TableCell>
                      <TableCell align="right">{l.overConsumption > 0 ? l.overConsumption : ''}</TableCell><TableCell>{l.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {check.notes.map(n => <Typography key={n} variant="caption" color="text.secondary" display="block">{n}</Typography>)}
            </Box>
          )}
        </Paper>
      )}

      <Dialog data-help-page="tax.coefficient.form.page" open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {current ? `Declaration ${current.productName} v${current.versionNo}` : 'New Mushak 4.3 declaration'}
          {current && <Chip size="small" sx={{ ml: 2 }} color={STATUS_COLOR[current.status]} label={current.status} />}
        </DialogTitle>
        <DialogContent dividers>
          {readOnly && <Alert severity="info" sx={{ mb: 2 }}>This declaration is {current?.status.toLowerCase()} and read-only.</Alert>}
          {warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: '1 1 140px', maxWidth: 180 }}>
              <TextField data-help="tax.coefficient.form.field.kind" select fullWidth size="small" label="Product is" value={kind} disabled={!!current || !editable}
                onChange={e => { const nk = e.target.value as CoefficientProductKind; setKind(nk); setProductId(''); setProductOption(null); setProductOptions([]); searchProducts('', nk); }}>
                <MenuItem value="STYLE">Garments style</MenuItem>
                <MenuItem value="ITEM">Item</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ flex: '1 1 280px', maxWidth: 420 }}>
              <SearchableSelect<PickOption> data-help="tax.coefficient.form.field.product" label="Product" options={productOptions} value={productId}
                selectedOption={productOption} disabled={!!current || !editable} disableClientFilter
                getOptionLabel={o => o.label}
                onChange={(id, o) => { setProductId(id); setProductOption(o); if (o?.unit) setUnit(o.unit); if (o?.hsCode) setHsCode(o.hsCode); }}
                onInputChange={t => debouncedSearchProducts(t)} data-testid="coef-product" />
            </Box>
            {editable && (
              <Button data-help="tax.coefficient.prefill" variant="outlined" onClick={doPrefill} disabled={!productId}>
                {kind === 'STYLE' ? 'From style BOM' : 'From item'}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: '1 1 120px', maxWidth: 160 }}>
              <TextField data-help="tax.coefficient.form.field.unit" fullWidth size="small" label="Unit of supply" value={unit} disabled={!editable} onChange={e => setUnit(e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
              <TextField data-help="tax.coefficient.form.field.hsCode" fullWidth size="small" label="HS code" value={hsCode} disabled={!editable} onChange={e => setHsCode(e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 160px', maxWidth: 200 }}>
              <TextField data-help="tax.coefficient.form.field.effectiveFrom" fullWidth size="small" type="date" label="Effective from" value={effectiveFrom} disabled={!editable}
                InputLabelProps={{ shrink: true }} onChange={e => setEffectiveFrom(e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
              <TextField data-help="tax.coefficient.form.field.price" fullWidth size="small" type="number" label="Supply price per unit (excl. VAT)" value={price}
                disabled={!editable} onChange={e => setPrice(e.target.value)} inputProps={{ 'data-testid': 'coef-price' }} />
            </Box>
            <Box sx={{ flex: '1 1 240px', maxWidth: 400 }}>
              <TextField data-help="tax.coefficient.form.field.notes" fullWidth size="small" label="Notes" value={notes} disabled={!editable} onChange={e => setNotes(e.target.value)} />
            </Box>
          </Box>

          <Typography variant="subtitle2">Inputs per unit of output</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table data-help-section="tax.coefficient.inputs" size="small" data-testid="coef-inputs">
              <TableHead>
                <TableRow>
                  <TableCell data-help="tax.coefficient.input.col.material" sx={{ minWidth: 240 }}>Input material</TableCell><TableCell data-help="tax.coefficient.input.col.uom">UoM</TableCell><TableCell data-help="tax.coefficient.input.col.qty">Qty / unit</TableCell>
                  <TableCell data-help="tax.coefficient.input.col.wastage">Wastage %</TableCell><TableCell data-help="tax.coefficient.input.col.price">Unit price</TableCell><TableCell data-help="tax.coefficient.input.col.value" align="right">Value</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, i) => (
                  <TableRow key={`${l.itemId}-${i}`}>
                    <TableCell>
                      <SearchableSelect<PickOption> data-help="tax.coefficient.input.item" size="small" options={itemOptions} value={l.itemId} disabled={!editable}
                        selectedOption={l.itemId ? { id: l.itemId, label: l.itemName } : null} disableClientFilter
                        getOptionLabel={o => o.label}
                        onChange={(id, o) => setLines(ls => ls.map((x, j) => (j === i ? { ...x, itemId: id, itemName: o?.label ?? '', uom: o?.unit ?? x.uom } : x)))}
                        onInputChange={debouncedSearchItems} />
                    </TableCell>
                    <TableCell>{l.uom}</TableCell>
                    {(['quantityPerUnit', 'wastagePercent', 'unitPurchasePrice'] as const).map(f => (
                      <TableCell key={f}>
                        <TextField data-help="tax.coefficient.input.cell" size="small" type="number" value={l[f]} disabled={!editable} sx={{ width: 110 }}
                          onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, [f]: e.target.value } : x)))} />
                      </TableCell>
                    ))}
                    <TableCell align="right">{formatMoney(lineValue(l))}</TableCell>
                    <TableCell>{editable && <IconButton data-help="tax.coefficient.input.remove" size="small" aria-label="Remove input" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={5}><strong>Total input cost per unit</strong></TableCell>
                  <TableCell align="right" data-testid="coef-input-cost"><strong>{formatMoney(inputCost)}</strong></TableCell><TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          {editable && <Button data-help="tax.coefficient.input.add" size="small" startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, { itemId: '', itemName: '', uom: '', quantityPerUnit: '', wastagePercent: '0', unitPurchasePrice: '' }])}>Add input</Button>}

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Value addition per unit</Typography>
          <Table data-help-section="tax.coefficient.valueAdds" size="small" data-testid="coef-value-adds">
            <TableBody>
              {valueAdds.map((v, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField data-help="tax.coefficient.valueAdd.kind" select size="small" value={v.kind} disabled={!editable} sx={{ minWidth: 160 }}
                      onChange={e => setValueAdds(vs => vs.map((x, j) => (j === i ? { ...x, kind: e.target.value as ValueAddKind } : x)))}>
                      {VALUE_ADD_KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField data-help="tax.coefficient.valueAdd.amount" size="small" type="number" label="Amount" value={v.amountPerUnit} disabled={!editable} sx={{ width: 130 }}
                      onChange={e => setValueAdds(vs => vs.map((x, j) => (j === i ? { ...x, amountPerUnit: e.target.value } : x)))} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="tax.coefficient.valueAdd.note" size="small" label="Note" value={v.note} disabled={!editable}
                      onChange={e => setValueAdds(vs => vs.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} />
                  </TableCell>
                  <TableCell>{editable && <IconButton data-help="tax.coefficient.valueAdd.remove" size="small" aria-label="Remove value addition" onClick={() => setValueAdds(vs => vs.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {editable && <Button data-help="tax.coefficient.valueAdd.add" size="small" startIcon={<AddIcon />} onClick={() => setValueAdds(vs => [...vs, { kind: 'WAGES', amountPerUnit: '', note: '' }])}>Add value addition</Button>}

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">Total cost per unit: <strong data-testid="coef-total-cost">{formatMoney(inputCost + addCost)}</strong></Typography>
            <Typography variant="body2">Supply price less total cost: <strong>{formatMoney(num(price) - inputCost - addCost)}</strong></Typography>
            {current?.vatOfficeReference && <Typography variant="body2">VAT office reference: {current.vatOfficeReference}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button data-help="tax.coefficient.form.close" onClick={() => setOpen(false)}>Close</Button>
          {current && canManage && current.status === 'DRAFT' && (
            <Button data-help="tax.coefficient.action.delete" color="error" disabled={busy} onClick={() => run(() => MushakFormsService.deleteCoefficient(institutionId, current.id), 'Declaration deleted', true)}>Delete</Button>
          )}
          {editable && <Button data-help="tax.coefficient.action.saveDraft" variant="outlined" disabled={busy || !productId} onClick={save}>Save draft</Button>}
          {current && canManage && current.status === 'DRAFT' && (
            <Button data-help="tax.coefficient.action.submit" variant="contained" disabled={busy} onClick={() => run(() => MushakFormsService.submitCoefficient(institutionId, current.id), 'Declaration submitted')}>Submit</Button>
          )}
          {current && canManage && current.status === 'SUBMITTED' && (
            <>
              <Button data-help="tax.coefficient.action.returnDraft" disabled={busy} onClick={() => run(() => MushakFormsService.returnCoefficientToDraft(institutionId, current.id), 'Returned to draft')}>Return to draft</Button>
              <Button data-help="tax.coefficient.action.accept" variant="contained" color="success" disabled={busy} onClick={() => { setOfficeRef(''); setAcceptOpen(true); }}>Mark accepted</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog data-help-page="tax.coefficient.accept.page" open={acceptOpen} onClose={() => setAcceptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark accepted by the VAT office</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Accepting supersedes the previous accepted declaration of this product.</Typography>
          <TextField data-help="tax.coefficient.accept.field.officeRef" fullWidth autoFocus size="small" label="VAT office reference" value={officeRef} onChange={e => setOfficeRef(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button data-help="tax.coefficient.accept.cancel" onClick={() => setAcceptOpen(false)}>Cancel</Button>
          <Button data-help="tax.coefficient.accept.submit" variant="contained" disabled={!officeRef.trim() || busy} onClick={async () => {
            setAcceptOpen(false);
            if (current) await run(() => MushakFormsService.acceptCoefficient(institutionId, current.id, officeRef.trim()), 'Declaration accepted');
          }}>Accept</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MushakCoefficientPage;
